import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignService } from 'src/app/private/services/campaign.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// Interfaces
interface TrackingEvent {
  id: string;
  campaign_id: string;
  client_id: string;
  message_id: string;
  email_to: string;
  email_from: string;
  event_type: string;
  status: string;
  bounce_type: string | null;
  bounce_sub_type: string | null;
  complaint_feedback_type: string | null;
  timestamp: string;
  raw_event_data: string;
  createdAt: string;
  updatedAt: string;
}

interface CampaignStatistics {
  total_needs_to_be_sent: number;  // Total to send
  queued_count: number;             // Still in queue
  total: number;                    // Actually sent (delivered)
  sent: number;                     // Sent count
  delivered: number;                // Delivered count
  bounced: number;
  complained: number;
  opened: number;
  clicked: number;
  rejected: number;
}

interface CampaignMetadata {
  sent: number;
  total: number;
  failed: number;
  queued: number;
  message: string;
  suppressed: number;
  frequencySkipped: number;
}

interface CampaignAnalytics {
  tracking_data: TrackingEvent[];
  statistics: CampaignStatistics;
  campaign_metadata?: CampaignMetadata;
}

interface StatCard {
  label: string;
  value: number;
  percentage: number;
  icon: string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

@Component({
  selector: 'app-email-campaign-analytics',
  templateUrl: './email-campaign-analytics.component.html',
  styleUrls: ['./email-campaign-analytics.component.scss']
})
export class EmailCampaignAnalyticsComponent implements OnInit {
  isLoading = true;
  campaignId: string = '';
  showQueueInfo = false;  // Toggle for queue info tooltip

  analyticsData: CampaignAnalytics | null = null;
  statistics: CampaignStatistics | null = null;
  trackingEvents: TrackingEvent[] = [];
  campaignMetadata: CampaignMetadata | null = null;

  get campaignCompletionPercentage(): number {
    if (!this.statistics || !this.statistics.total_needs_to_be_sent) return 0;
    const totalSent = (this.statistics.sent || 0) + (this.statistics.bounced || 0) + (this.statistics.rejected || 0);
    return Math.round((totalSent / this.statistics.total_needs_to_be_sent) * 100);
  }

  get campaignRemainingInQueue(): number {
    return this.statistics?.queued_count || 0;
  }

  statCards: StatCard[] = [];
  selectedEventType: string = 'all';
  eventTypes: string[] = ['all', 'Send', 'Delivery', 'Open', 'Click', 'Bounce', 'Complaint'];

  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 1;

  timelineChartInstance: any = null;
  funnelChartInstance: any = null;
  donutChartInstance: any = null;

  viewMode: 'table' | 'graph' = 'table';

  constructor(
    private campaignService: CampaignService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.params['id'];
    this.getCampaignTracking();
  }

  getCampaignTracking() {
    this.isLoading = true;
    const data = {
      hospital_id: this.localStorageService.getHospitalId(),
      campaign_id: this.campaignId
    };

    this.campaignService.getCampaignTracking(data).subscribe({
      next: (res: any) => {
        this.analyticsData = res;
        this.statistics = res.statistics;
        this.trackingEvents = res.tracking_data || [];
        this.campaignMetadata = {
          sent: res.statistics?.sent || 0,
          total: res.statistics?.total_needs_to_be_sent || 0,
          failed: res.statistics?.rejected || 0,
          queued: res.statistics?.queued_count || 0,
          message: '',
          suppressed: 0,
          frequencySkipped: 0
        };
        this.calculateStatCards();
        this.updatePagination();
        this.isLoading = false;
        setTimeout(() => { this.initAllCharts(); }, 100);
      },
      error: (err) => {
        console.error('Error fetching campaign analytics:', err);
        this.isLoading = false;
      }
    });
  }

  calculateStatCards() {
    if (!this.statistics) return;

    const safePct : any = (numerator: number, divisor: number): number =>
      divisor > 0 ? (numerator / divisor) * 100 : 0;

    const sent      = this.statistics.sent      || 0;
    const delivered = this.statistics.delivered  || 0;
    const total     = this.statistics.total      || 0;
    // Use unique email counts from tracking events for Opened & Clicked
    const opened  = this.uniqueOpenedCount;
    const clicked = this.uniqueClickedCount;

    this.statCards = [
      { label: 'Total Sent',  value: sent,      percentage: safePct(sent, total),           icon: 'fas fa-paper-plane',           color: '#0D4259', trend: 'neutral' },
      { label: 'Delivered',   value: delivered, percentage: safePct(delivered, sent),        icon: 'fas fa-check-circle',          color: '#10b981', trend: 'up' },
      { label: 'Opened',      value: opened,    percentage: safePct(opened, delivered),      icon: 'fas fa-envelope-open',         color: '#3b82f6', trend: 'up' },
      { label: 'Clicked',     value: clicked,   percentage: safePct(clicked, delivered),     icon: 'fas fa-mouse-pointer',         color: '#8b5cf6', trend: 'neutral' },
      { label: 'Bounced',     value: this.statistics.bounced,    percentage: safePct(this.statistics.bounced, sent),         icon: 'fas fa-exclamation-triangle',  color: '#f59e0b', trend: 'down' },
      { label: 'Complained',  value: this.statistics.complained, percentage: safePct(this.statistics.complained, delivered), icon: 'fas fa-flag',                  color: '#ef4444', trend: 'down' }
    ];
  }

  initAllCharts() {
    if (!this.statistics) return;
    this.prepareTimelineData();
    this.initFunnelChart();
    this.initDonutChart();
  }

  initFunnelChart() {
    if (this.funnelChartInstance) this.funnelChartInstance.destroy();
    const canvas = document.getElementById('emailFunnelChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.funnelChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Sent', 'Delivered', 'Opened', 'Clicked'],
        datasets: [{
          label: 'Conversion',
          data: [this.statistics?.sent || 0, this.statistics?.delivered || 0, this.statistics?.opened || 0, this.statistics?.clicked || 0],
          backgroundColor: ['rgba(13,66,89,0.8)', 'rgba(16,185,129,0.8)', 'rgba(59,130,246,0.8)', 'rgba(139,92,246,0.8)'],
          borderRadius: 4,
          barPercentage: 0.6
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          title: { display: true, text: 'Campaign Conversion Funnel', padding: 20 }
        },
        scales: {
          x: { display: false },
          y: { grid: { display: false } }
        }
      }
    });
  }

  initDonutChart() {
    if (this.donutChartInstance) this.donutChartInstance.destroy();
    const canvas = document.getElementById('emailDonutChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.donutChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Delivered', 'Bounced', 'Complained'],
        datasets: [{ data: [this.statistics?.delivered || 0, this.statistics?.bounced || 0, this.statistics?.complained || 0], backgroundColor: ['#10b981', '#f59e0b', '#ef4444'], hoverOffset: 4 }]
      },
      options: {
        responsive: true,
        cutout: '70%',
        plugins: {
          legend: { position: 'right' },
          title: { display: true, text: 'Delivery Status', padding: 20 }
        }
      }
    });
  }

  prepareTimelineData() {
    if (!this.trackingEvents.length) return;
    if (this.timelineChartInstance) this.timelineChartInstance.destroy();

    const filtered = this.filteredEvents;
    const sortedEvents = [...filtered].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const isSameDay = sortedEvents.length > 1 &&
      new Date(sortedEvents[0].timestamp).toDateString() === new Date(sortedEvents[sortedEvents.length - 1].timestamp).toDateString();

    const timeLabels: string[] = [];
    const timeMap: { [key: string]: number } = {};
    const getTimeKey = (timestamp: string) => {
      const date = new Date(timestamp);
      if (isSameDay) return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    sortedEvents.forEach(event => {
      const key = getTimeKey(event.timestamp);
      if (!timeMap.hasOwnProperty(key)) { timeMap[key] = 0; timeLabels.push(key); }
    });

    let datasets: any[] = [];

    if (this.selectedEventType === 'all') {
      const metrics = ['Send', 'Delivery', 'Open', 'Click', 'Bounce'];
      const colors: { [key: string]: string } = { 'Send': '#0D4259', 'Delivery': '#10b981', 'Open': '#3b82f6', 'Click': '#8b5cf6', 'Bounce': '#f59e0b' };
      datasets = metrics.map(metric => ({
        label: metric === 'Send' ? 'Sent' : metric === 'Delivery' ? 'Delivered' : metric + 'ed',
        data: timeLabels.map(label => this.trackingEvents.filter(e => getTimeKey(e.timestamp) === label && e.event_type.toLowerCase() === metric.toLowerCase()).length),
        borderColor: colors[metric],
        backgroundColor: colors[metric],
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointBorderColor: colors[metric],
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.4
      }));
    } else {
      const colorMap: { [key: string]: { border: string, bg: string } } = {
        'send':      { border: '#0D4259', bg: 'rgba(13,66,89,0.1)' },
        'delivery':  { border: '#10b981', bg: 'rgba(16,185,129,0.1)' },
        'open':      { border: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
        'click':     { border: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
        'bounce':    { border: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
        'complaint': { border: '#ef4444', bg: 'rgba(239,68,68,0.1)' }
      };
      let borderColor = '#0D4259', backgroundColor = 'rgba(13,66,89,0.1)';
      const colors = colorMap[this.selectedEventType.toLowerCase()];
      if (colors) { borderColor = colors.border; backgroundColor = colors.bg; }
      datasets = [{
        label: this.selectedEventType,
        data: timeLabels.map(label => filtered.filter(e => getTimeKey(e.timestamp) === label).length),
        borderColor, backgroundColor,
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointBorderColor: borderColor,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        tension: 0.4
      }];
    }

    this.initTimelineChart(timeLabels, datasets);
  }

  initTimelineChart(labels: string[], datasets: any[]) {
    const canvas = document.getElementById('emailTimelineChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.timelineChartInstance = new Chart(ctx, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: datasets.length > 1, position: 'top', labels: { usePointStyle: true, boxWidth: 8, padding: 20 } },
          tooltip: {
            backgroundColor: 'rgba(255,255,255,0.95)',
            titleColor: '#1f2937',
            bodyColor: '#4b5563',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            padding: 12,
            boxPadding: 4,
            usePointStyle: true,
            titleFont: { size: 13, weight: 'bold' }
          }
        },
        scales: {
          y: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { stepSize: 1 } },
          x: { grid: { display: false } }
        }
      }
    });
  }

  setViewMode(mode: 'table' | 'graph') {
    this.viewMode = mode;
    if (mode === 'graph') setTimeout(() => { this.initAllCharts(); }, 0);
  }

  get uniqueOpenedCount(): number {
    return new Set(
      this.trackingEvents
        .filter(e => e.event_type.toLowerCase() === 'open')
        .map(e => e.email_to)
    ).size;
  }

  get uniqueClickedCount(): number {
    return new Set(
      this.trackingEvents
        .filter(e => e.event_type.toLowerCase() === 'click')
        .map(e => e.email_to)
    ).size;
  }

  get isGroupedView(): boolean {
    return this.selectedEventType === 'Open' || this.selectedEventType === 'Click';
  }

  get filteredEvents(): TrackingEvent[] {
    if (this.selectedEventType === 'all') return this.trackingEvents;
    return this.trackingEvents.filter(event => event.event_type.toLowerCase() === this.selectedEventType.toLowerCase());
  }

  /** For Open/Click: deduplicate by email_to and include a `count` property. */
  get groupedFilteredEvents(): any[] {
    if (!this.isGroupedView) return this.filteredEvents;
    const map = new Map<string, any>();
    this.filteredEvents.forEach(event => {
      const key = event.email_to;
      if (!map.has(key)) {
        map.set(key, { ...event, count: 1 });
      } else {
        const existing = map.get(key);
        existing.count++;
        if (new Date(event.timestamp) > new Date(existing.timestamp)) {
          existing.timestamp = event.timestamp;
          existing.message_id = event.message_id;
        }
      }
    });
    return Array.from(map.values());
  }

  get paginatedEvents(): any[] {
    const events = this.isGroupedView ? this.groupedFilteredEvents : (this.filteredEvents as any[]);
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return events.slice(startIndex, startIndex + this.itemsPerPage);
  }

  updatePagination() {
    const total = this.isGroupedView ? this.groupedFilteredEvents.length : this.filteredEvents.length;
    this.totalPages = Math.ceil(total / this.itemsPerPage);
  }

  setEventFilter(eventType: string) {
    this.selectedEventType = eventType;
    this.currentPage = 1;
    this.updatePagination();
    this.prepareTimelineData();
  }

  nextPage()     { if (this.currentPage < this.totalPages) this.currentPage++; }
  previousPage() { if (this.currentPage > 1) this.currentPage--; }
  goToPage(page: number) { this.currentPage = page; }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'sent': 'badge-info', 'delivered': 'badge-success', 'opened': 'badge-primary',
      'clicked': 'badge-purple', 'bounced': 'badge-warning', 'complained': 'badge-danger', 'rejected': 'badge-danger'
    };
    return statusMap[status.toLowerCase()] || 'badge-default';
  }

  getEventIcon(eventType: string): string {
    const iconMap: { [key: string]: string } = {
      'send': 'bi-send-fill', 'delivery': 'bi-check-circle-fill', 'open': 'bi-envelope-open-fill',
      'click': 'bi-cursor-fill', 'bounce': 'bi-exclamation-triangle-fill', 'complaint': 'bi-flag-fill', 'reject': 'bi-x-circle-fill'
    };
    return iconMap[eventType.toLowerCase()] || 'bi-info-circle-fill';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  formatPercentage(value: number): string { return value.toFixed(1); }

  viewRawData(event: TrackingEvent) {
    try { alert(JSON.stringify(JSON.parse(event.raw_event_data), null, 2)); }
    catch (error) { console.error('Error parsing raw event data:', error); }
  }

  exportData() {
    const dataStr = JSON.stringify(this.analyticsData, null, 2);
    const url = URL.createObjectURL(new Blob([dataStr], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `email-campaign-${this.campaignId}-analytics.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  get dispatchProgress(): number {
    if (!this.campaignMetadata || !this.campaignMetadata.total) return 0;
    return Math.round((this.campaignMetadata.sent / this.campaignMetadata.total) * 100);
  }

  get isPartialDispatch(): boolean {
    return !!this.campaignMetadata && this.campaignMetadata.queued > 0;
  }

  goBack()      { this.router.navigate(['main/broadcast-campaign']); }
  refreshData() { this.getCampaignTracking(); }
}
